import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, CardTitle, Col, Container, Form, Input, Label, Row, FormFeedback } from "reactstrap";
import * as yup from "yup";
import { useFormik } from "formik";
import Breadcrumb from 'Components/UI/Breadcrumb';
import Editor from 'Components/Form/Editor/Editor';
import Select from "react-select";
import { useNavigate, useParams } from 'react-router-dom';
import { getPage, updatePage } from 'helpers/API Calls/System & Settings/Pages';
import { getLocations } from 'helpers/API Calls/System & Settings/Locations';
import Spinner from 'Components/UI/Spinner';
import { STATUS_OPTIONS } from 'pages/Categories/data';
import Dropzone from 'Components/Form/DropZone';
import placeholderImage from "assets/images/img.png"
import Hint from 'Components/UI/Hint';

interface PageFormValues {
  title: string;
  sub_title: string;
  image_caption: string;
  content: string;
  is_seo: boolean;
  meta_title: string;
  meta_keywords: string;
  meta_description: string;
  location: { value: number; label: string } | null;
  status: any;
  is_featured: boolean;
  deleteImage: boolean;
}

function EditPage() {

  const navigate = useNavigate();
  const {id} = useParams<{ id: string }>();
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [dropdownOptions, setDropdownOptions] = useState({
    locations: []
  });
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [image, setImage] = useState<string | null>(null);

  const formik = useFormik<PageFormValues>({    
    initialValues: {
      title: '',
      sub_title: '',
      image_caption: '',
      content: '',
      is_seo: false,
      meta_title: '',
      meta_keywords: '',
      meta_description: '',
      location: null,
      status: null, 
      is_featured: false,
      deleteImage: false,
    },
    validationSchema: yup.object().shape({
      title: yup.string().required('Title is required'),
      sub_title: yup.string().required('Sub title is required'),
      image_caption: yup.string().nullable(),      
      location: yup.mixed().nullable(),
      meta_title: yup.string().when('is_seo', { 
        is: true, 
        then: schema => schema.required('Meta title is required when SEO is enabled')
      }),
      meta_keywords: yup.string().when('is_seo', { 
        is: true, 
        then: schema => schema.required('Meta keywords are required when SEO is enabled')
      }),
      meta_description: yup.string().when('is_seo', {
        is: true, 
        then: schema => schema.required('Meta description is required when SEO is enabled')
      }),
      status : yup.object().shape({label: yup.string().required(), value: yup.string().required(),}).nullable().required('Please select status'),
    }),    
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
       const pageData = {
        title: values.title,
        sub_title: values.sub_title,
        image_caption: values.image_caption,
        content,
        is_seo: values.is_seo ? 1 : 0,
        meta_title: values.meta_title,
        meta_keywords: values.meta_keywords,
        meta_description: values.meta_description,
        page_type_id: 1,
        location_id: values.location?.value,
        is_featured: values.is_featured ? 1 : 0,
        status: values.status?.value === 'active' ? 1 : 0,
        ...(values.deleteImage && { no_image: 1 })
      };

      const response = await updatePage(id || '', { image: selectedImage, data: pageData });
      if (response.success) {
        navigate('/pages/locations');
      }
      setSubmitting(false);
    },
  });

    // Add scroll to error effect
    useEffect(() => {
      if (Object.keys(formik.errors).length > 0 && formik.isSubmitting) {
        const firstErrorKey = Object.keys(formik.errors)[0];
        const element = document.getElementById(firstErrorKey);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.focus();
        }
      }
    }, [formik.errors, formik.isSubmitting]);

  useEffect(() => {
    const fetchData = async () => {
        setIsLoading(true);

        const locations = await getLocations('limit=1000');
        if(locations.success) {
            const locationOptions = locations.data?.items?.map((location: any) => ({
                value: location.id,
                label: location.location_name
            }));
            setDropdownOptions(prev => ({ ...prev, locations: locationOptions }));
        }        
        const pageData = await getPage(Number(id));
        if (pageData.success) {
            const data = pageData.data;
            formik.setValues({
                title: data.title,
                sub_title: data.sub_title,
                image_caption: data.image_caption,
                content: data.content,
                is_seo: data.is_seo === '1',
                meta_title: data.meta_title || '',
                meta_keywords: data.meta_keywords || '',
                meta_description: data.meta_description || '',
                location: data.location ? { value: data.location.id, label: data.location.location_name } : null,
                status: data?.status === 1 ? {label: 'Active', value: 'active'} : {label: 'Inactive', value: 'inactive'},
                is_featured: data.is_featured === '1', 
                deleteImage: false,
            });
            setContent(data.content);
            setImage(data?.image);
            setIsLoading(false);
        }
    }
    fetchData(); 
  }, [id])

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Location Pages", link: "/pages/locations" }, {label: 'Edit'}]} />
        {isLoading ? <div className="position-relative vh-100"><Spinner/></div>
        :
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              {/* Basic Information */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Basic Information</CardTitle>
                <Row>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="title">Title <span className="text-danger">*</span></Label>
                      <Input
                        id="title"
                        name="title"
                        type="text"
                        placeholder="Enter page title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        invalid={formik.touched.title && formik.errors.title ? true : false}
                      />
                      {formik.errors.title && formik.touched.title && (
                        <FormFeedback type="invalid">{formik.errors.title}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">
                      <Label htmlFor="sub_title">Sub Title <span className="text-danger">*</span></Label>
                      <Input
                        id="sub_title"
                        name="sub_title"
                        type="text"
                        placeholder="Enter page sub title"
                        value={formik.values.sub_title}
                        onChange={formik.handleChange}
                        invalid={formik.touched.sub_title && formik.errors.sub_title ? true : false}
                      />
                      {formik.errors.sub_title && formik.touched.sub_title && (
                        <FormFeedback type="invalid">{formik.errors.sub_title}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col md="6">
                    <div className="mb-3">                      
                      <Label htmlFor="location">Location</Label>
                      <Select
                        id="location"
                        name="location"
                        placeholder="Select Location"
                        options={dropdownOptions.locations}
                        value={formik.values.location}
                        onChange={(value) => {
                          formik.setFieldValue("location", value);
                        }}
                        onBlur={formik.handleBlur}
                        className={formik.touched.location && formik.errors.location ? "is-invalid" : ""}
                        // isLoading={isLoading}
                        isClearable
                      />
                      {formik.touched.location && formik.errors.location && (
                        <div className="invalid-feedback d-block">
                          {typeof formik.errors.location === 'string' && formik.errors.location}
                        </div>
                      )}
                    </div>
                  </Col>
                   <Col md="6">
                        <div className="mb-3">
                        <Label htmlFor="image_caption">Image Caption</Label>
                        <Input
                            id="image_caption"
                            name="image_caption"
                            type="text"
                            placeholder="Enter image caption"
                            value={formik.values.image_caption}
                            onChange={formik.handleChange}
                            invalid={formik.touched.image_caption && formik.errors.image_caption ? true : false}
                        />
                        {formik.errors.image_caption && formik.touched.image_caption && (
                            <FormFeedback type="invalid">{formik.errors.image_caption}</FormFeedback>
                        )}
                        </div>
                    </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <div className="mb-3" id="featured-image-section">
                        <Label htmlFor="name-input">Featured Image</Label>
                        <Dropzone
                        accept = {{"image/*": [".png", ".jpeg", ".jpg"]}}
                        onDrop = {(acceptedFiles) => {
                            setSelectedImage(acceptedFiles[0]);
                            formik.setFieldValue('deleteImage', false);
                        }}
                        onDelete={() => {setSelectedImage(null); formik.setFieldValue('deleteImage', true); setImage(null);}}
                        showDelete={!!selectedImage || !!image}
                        renderContent = {() => (
                            <div className="text-center cursor-pointer">
                                <div className="position-relative d-inline-block" >
                                    <div className={`avatar-xl rounded`}>
                                        <div className="avatar-title bg-light ">
                                            <img
                                                src={selectedImage ? URL.createObjectURL(selectedImage) : image || placeholderImage}
                                                id="logo-img"
                                                alt=""
                                                className="avatar-xl rounded"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        />
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Content */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Page Content</CardTitle>
                <div className="mb-3">
                  <Editor content={content} setContent={setContent} />
                </div>
              </div>

              {/* SEO Information */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>SEO Information</CardTitle>
                <div className="mb-3">
                  <div className="form-check mb-3">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="is_seo"
                      name="is_seo"
                      checked={formik.values.is_seo}
                      onChange={formik.handleChange}
                    />
                    <Label className="form-check-label" htmlFor="is_seo">
                      Index this page in search engines <Hint type="index" />
                    </Label>
                  </div>
                </div>
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="meta_title">
                        Meta Title {formik.values.is_seo && <span className="text-danger">*</span>} <Hint type="title" />
                      </Label>
                      <Input
                        id="meta_title"
                        name="meta_title"
                        type="text"
                        placeholder="Enter meta title"
                        value={formik.values.meta_title}
                        onChange={formik.handleChange}
                        invalid={formik.touched.meta_title && formik.errors.meta_title ? true : false}
                      />
                      {formik.errors.meta_title && formik.touched.meta_title && (
                        <FormFeedback type="invalid">{formik.errors.meta_title}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="meta_keywords">
                        Meta Keywords {formik.values.is_seo && <span className="text-danger">*</span>} <Hint type="keywords" />
                      </Label>
                      <Input
                        id="meta_keywords"
                        name="meta_keywords"
                        type="text"
                        placeholder="Enter meta keywords"
                        value={formik.values.meta_keywords}
                        onChange={formik.handleChange}
                        invalid={formik.touched.meta_keywords && formik.errors.meta_keywords ? true : false}
                      />
                      {formik.errors.meta_keywords && formik.touched.meta_keywords && (
                        <FormFeedback type="invalid">{formik.errors.meta_keywords}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col sm="12">
                    <div className="mb-3">
                      <Label htmlFor="meta_description">
                        Meta Description {formik.values.is_seo && <span className="text-danger">*</span>} <Hint type="description" />
                      </Label>
                      <Input
                        id="meta_description"
                        name="meta_description"
                        type="textarea"
                        rows="3"
                        placeholder="Enter meta description"
                        value={formik.values.meta_description}
                        onChange={formik.handleChange}
                        invalid={formik.touched.meta_description && formik.errors.meta_description ? true : false}
                      />
                      {formik.errors.meta_description && formik.touched.meta_description && (
                        <FormFeedback type="invalid">{formik.errors.meta_description}</FormFeedback>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Page Settings */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Page Settings</CardTitle>
                <Row>
                <Col sm={6}>
                    <div className="mb-4">
                    <Label className="form-label">Status <span className="text-danger">*</span></Label>
                    <Select
                        name="status"
                        placeholder="Select Status"
                        options={STATUS_OPTIONS}
                        value={formik.values.status}
                        onChange={(value) => formik.setFieldValue("status", value)}
                        onBlur={formik.handleBlur}
                        className={formik.touched.status && formik.errors.status ? "is-invalid" : ""}
                    />
                    {formik.touched.status && formik.errors.status ? (
                        <FormFeedback type="invalid">{typeof formik.errors.status === 'string' && formik.errors.status}</FormFeedback>
                    ) : null}
                    </div>
                </Col>
                <Col sm={12}>
                    <div className="mb-3">
                        <div className="form-check mb-2">
                        <Input
                            type="checkbox"
                            className="form-check-input"
                            id="is_featured"
                            name="is_featured"
                            checked={formik.values.is_featured}
                            onChange={formik.handleChange}
                        />
                        <Label className="form-check-label" htmlFor="is_featured">
                            Featured Page
                        </Label>
                        </div>
                    </div>
                </Col>
                  
                </Row>
              </div>

              <div className="d-flex mb-3 justify-content-end">
                <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Updating...' : 'Update Page'}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
        }
      </Container>
    </div>
  );
}

export default EditPage;
